import { createContext, useContext, useEffect, useState } from "react";
import { v4 as randomUUID } from "uuid";
import { MusicInfo } from "../../../types/music";
import reloadPage from "../../utils/AndroidBridge";
import CustomWebSocket from "../../utils/customWebSocket";
import { isInsideDashboard } from "../../utils/isInsideDashboard";
import { useAuth } from "../useAuth";
import { useLogs } from "../useLogs";
import { toPng } from 'html-to-image';

let _socketSingleton: CustomWebSocket | null = null;
export function getSocketInstance() {
  return _socketSingleton;
}

export function useWebSocket(url: string, { refetchOnVisibile = true, refetchOnOnline = true } = {}) {
  const [socket, setSocket] = useState<CustomWebSocket>()

  function createSocket() {
    const socket = new CustomWebSocket(url)
    setSocket(socket)
  }

  useEffect(() => {
    createSocket()
  }, [])

  useEffect(() => {
    return () => {
      socket?.close()
    }
  }, [socket])

  useEffect(() => {
    if (!refetchOnVisibile) return;

    const abortController = new AbortController()

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        createSocket()
      }
    }, abortController)

    return () => {
      abortController.abort()
    }
  }, [refetchOnVisibile])

  useEffect(() => {
    if (!refetchOnOnline) return;

    const abortController = new AbortController()

    window.addEventListener("online", () => {
      createSocket()
    }, abortController)

    return () => {
      abortController.abort()
    }
  }, [refetchOnOnline])

  return [socket, createSocket] as const
}

const screenInfoContext = createContext<ScreenSocketInfoReturnProps | null>(
  null,
);
const { Provider } = screenInfoContext;
type Props = {
  children: React.ReactNode;
  onScreenChanged: (screenInfo: any) => void;
  onLayoutChanged: (layoutInfo: any) => void;
  onPlaylistChanged: (playlistInfo: any) => void;
  onMusicChanged: (musicInfo: MusicInfo[] | undefined, action?: 'pause' | 'resume' | 'disable' | 'enable') => void;
  onMusicPaused: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicResumed: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicDisabled: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicEnabled: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicSyncEnabled: () => void;
  onMusicSyncDisabled: () => void;
};
export function ScreenSocketInfoProvider({
  children,
  onScreenChanged,
  onLayoutChanged,
  onPlaylistChanged,
  onMusicDisabled,
  onMusicEnabled,
  onMusicPaused,
  onMusicResumed,
  onMusicChanged,
  onMusicSyncEnabled,
  onMusicSyncDisabled,
}: Props) {
  const { deviceId, orgId, screenInfo: _screenInfo } = useAuth();

  const [isOnline, setIsOnline] = useState(false);

  const { log } = useLogs();

  const [socket, reconnectSocket] = useWebSocket(
    import.meta.env.VITE_WS_URL! +
    "/" +
    import.meta.env.VITE_WS_AUTHENTICATED_SCREEN_NAMESPACE! +
    "/public/websocket",
    {
      refetchOnVisibile: false,
      refetchOnOnline: false,
    }
  )

  useEffect(() => {
    function setupSocket() {
      if (!socket) return
      setIsOnline(false);

      const _previousUUid = localStorage.getItem("previous_uuid");

      const uuid = _previousUUid ?? randomUUID();
      localStorage.setItem("uuid", uuid);

      socket.onOpen = () => {
        setIsOnline(false);

        if (deviceId)
          socket.emit(
            "joinRoom",
            {
              roomId: deviceId,
              id: deviceId,
              uniqueId: uuid,
              orgId: orgId,
              isFromReload: _previousUUid ? true : false,
              from: isInsideDashboard() ? "dashboard" : "player",
            },
            () => {
              setIsOnline(true);
              localStorage.removeItem("previous_uuid");
            },
          );

        log({
          name: "SocketConnected",
          eventtype: "SocketEvent",
          payload: {
            deviceId,
          },
        });
      };

      socket.onClose = () => {
        setIsOnline(false);
        log({
          name: "PairSocketDisconnected",
          eventtype: "SocketEvent",
          payload: {
            deviceId,
          },
        });
        reconnectSocket()
      };

      socket.onError = () => {
        setIsOnline(false);
      };

      // Function to handle screenshot request
      const handleScreenshotRequest = async () => {
        try {
          // Get the main content container to capture - typically the body or a main element
          const targetElement = document.getElementById('renderRoot');

          if (!targetElement) {
            log({
              name: "ScreenshotError",
              eventtype: "Error",
              payload: {
                error: "Target element not found for screenshot",
              },
            });
            return;
          }

          // Convert the element to PNG image
          const dataUrl = await toPng(targetElement as HTMLElement, {
            backgroundColor: '#ffffff',
          });

          // Extract the base64 data from the data URL
          const base64Data = dataUrl.replace(/^data:image\/png;base64,/, "");

          // Send the screenshot data to the server via HTTP request to upload to Cloudflare
          try {
            const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/uploadScreenshot`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}` // Assuming auth token is stored
              },
              body: JSON.stringify({
                screenshot: base64Data,
                screen_id: deviceId,
                org_id: orgId,
                timestamp: Date.now()
              })
            });

            if (response.ok) {
              const result = await response.json();
              log({
                name: "ScreenshotUploaded",
                eventtype: "ScreenshotEvent",
                payload: {
                  deviceId: deviceId,
                  orgId: orgId,
                  assetId: result.id
                },
              });

              // Emit success event back to the main app/dashboard
              socket?.emit("broadcastMessage", {
                roomId: deviceId ?? "",
                messageContent: "screenshotUploaded",
                messageData: {
                  from: "player",
                  orgId: orgId,
                  assetId: result.id,
                  url: result.url || result.result?.variants?.[0], // Cloudflare typically returns the URL
                  timestamp: Date.now()
                },
              })
            } else {
              throw new Error(`Upload failed with status ${response.status}`);
            }
          } catch (uploadError) {
            log({
              name: "ScreenshotUploadError",
              eventtype: "Error",
              payload: {
                error: uploadError,
              },
            });
          }

          log({
            name: "ScreenshotCaptured",
            eventtype: "ScreenshotEvent",
            payload: {
              deviceId: deviceId,
              orgId: orgId,
            },
          });
        } catch (error) {
          log({
            name: "ScreenshotError",
            eventtype: "Error",
            payload: {
              error: error,
            },
          });
        }
      };

      socket.onMessage = (message) => {
        try {
          const data = JSON.parse(message.data);
          const event = data.event;
          log({
            name: "SocketEvent",
            eventtype: "SocketEvent",
            payload: {
              data: data,
            },
          });

          switch (event) {
            case "broadcastMessage": {
              const _data = data.data;
              const isFromPlayer = _data.data?.from === "player"
              const playerId = _data.from
              if (isFromPlayer && playerId !== deviceId) {
                switch (_data.content) {
                  case "groupMusicPaused":
                    onMusicPaused(_data.data)
                    break
                  case "groupMusicResumed":
                    onMusicResumed(_data.data)
                }
              } else {
                switch (_data.content) {
                  case "restart":
                    reloadPage();
                    break;
                  case "musicPauseRequested":
                  case "groupMusicPauseRequested":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicPaused(_data.data)
                    break;
                  case "musicChanged":
                  case "groupMusicChanged":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicChanged(_data.data);
                    break;
                  case "musicResumeRequested":
                  case "groupMusicResumeRequested":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicResumed(_data.data);
                    break;
                  case "musicDisabled":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicDisabled(_data.data);
                    break;
                  case "musicEnabled":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicEnabled(_data.data);
                    break;
                  case "musicSyncEnabled":
                    onMusicSyncEnabled();
                    break;
                  case "musicSyncDisabled":
                    onMusicSyncDisabled()
                    break;
                  case "screenChanged":
                    log({
                      name: "ScreenChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data.data,
                      },
                    });
                    onScreenChanged(_data.data);
                    break;
                  case "layoutChanged":
                    log({
                      name: "LayoutChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: data.data,
                      },
                    });
                    onLayoutChanged(_data.data);
                    break;
                  case "playlistChanged":
                    log({
                      name: "PlaylistChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: data.data,
                      },
                    });
                    onPlaylistChanged(_data.data);
                    break;
                  case "screenshot":
                    log({
                      name: "ScreenshotRequested",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    // Trigger screenshot capture
                    handleScreenshotRequest();
                    break;
                }
              }
              break;
            }

            case "heartbeat":
              socket.emit("heartBeatBack", {});
              break;

            default:
              break;
          }
        } catch (e) {
          log({
            name: "SocketError",
            eventtype: "Error",
            payload: {
              error: e,
            },
          });
        }
      };
    }
    setupSocket()
    _socketSingleton = socket ?? null
  }, [socket, deviceId]);

  return (
    <Provider
      value={{
        socket: socket || null,
        isOnline,
      }}
    >
      {children}
    </Provider>
  );
}

interface ScreenSocketInfoReturnProps {
  socket: CustomWebSocket | null;
  isOnline: boolean;
}

export const useScreenSocketInfo: () => ScreenSocketInfoReturnProps = () => {
  const context = useContext(screenInfoContext);
  if (!context)
    throw "useScreenSocket must be used within a ScreenSocketProvider";
  return context;
};
